import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from './viewbid/state/app-state';
import { createdProfile, selectUser } from './viewbid/state/app-selectors';
import { TokenStorageService, User } from 'viewbid-shared-lib';
import { AuthService } from '@auth0/auth0-angular';
import { CreateProfileResult } from './viewbid/modules/profiles/state/create-profile-state';
import {
    ProfileService,
    ProfileStatus,
} from './viewbid/shared/services/profile.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    constructor(
        private router: Router,
        private store: Store<AppState>,
        private tokenStorageService: TokenStorageService,
        private authService: AuthService,
        private profileService: ProfileService,
    ) {}

    canActivate(): Observable<boolean | UrlTree> {
        return this.mustCreateProfile().pipe(
            map((mustCreateProfile: boolean) =>
                mustCreateProfile
                    ? this.router.createUrlTree(['/profiles/create-profile'])
                    : true,
            ),
        );
    }

    private mustCreateProfile(): Observable<boolean> {
        return this.store
            .select<CreateProfileResult | null>(createdProfile)
            .pipe(
                take(1),
                switchMap((result: CreateProfileResult | null) => {
                    if (result || this.tokenStorageService.isMasquerading()) {
                        this.profileService.updateUserStore();
                        return of(false);
                    }
                    return this.isSignedIn().pipe(
                        switchMap((isSignedIn: boolean) =>
                            isSignedIn
                                ? this.signedInUserHasNoProfile()
                                : of(false),
                        ),
                    );
                }),
            );
    }

    private isSignedIn(): Observable<boolean> {
        // isAuthenticated$ goes false once Auth0's cached session flag lapses, even while the refresh token is still valid
        return this.authService.isAuthenticated$.pipe(
            take(1),
            switchMap((isAuthenticated: boolean) =>
                isAuthenticated
                    ? of(true)
                    : this.authService.getAccessTokenSilently().pipe(
                          map(() => true),
                          catchError(() => of(false)),
                      ),
            ),
        );
    }

    private signedInUserHasNoProfile(): Observable<boolean> {
        return this.store.select<User | null>(selectUser).pipe(
            take(1),
            switchMap((user: User | null) => {
                if (user?.accountMappingsId) {
                    this.profileService.updateUserStore();
                    return of(false);
                }
                return this.profileService
                    .resolveProfileStatus()
                    .pipe(
                        map(
                            (status: ProfileStatus) =>
                                status === ProfileStatus.Incomplete,
                        ),
                    );
            }),
        );
    }
}
